import { useQuery } from "@tanstack/react-query";

import { IDeviceTypeOption } from "../../../@types/admin/AdminDevice";
import { ICodeOption } from "../../../@types/admin/AdminMedication";
import { IRoleOption, ISelectOption } from "../../../@types/admin/AdminStaff";
import { selectValuesService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/** Pharmaceutical forms for custom medications (EN-5240); ids are codes. */
export function useMedicationFormOptions() {
  const { data, isLoading, isError } = useQuery<ICodeOption[]>({
    queryKey: ["admin-select-medication-forms"],
    queryFn: async () =>
      ensureOk(await selectValuesService.getMedicationFormOptions()),
  });

  return { forms: data ?? [], isLoading, isError };
}

/** Languages translations can be written in (EN-5240); ids are codes. */
export function useLanguageOptions() {
  const { data, isLoading, isError } = useQuery<ICodeOption[]>({
    queryKey: ["admin-select-languages"],
    queryFn: async () =>
      ensureOk(await selectValuesService.getLanguageOptions()),
  });

  return { languages: data ?? [], isLoading, isError };
}

/** Hospital groups for the /admin dropdowns. Catalogue-sized and stable. */
export function useHospitalOptions() {
  const { data, isLoading, isError } = useQuery<ISelectOption[]>({
    queryKey: ["admin-select-hospitals"],
    queryFn: async () =>
      ensureOk(await selectValuesService.getHospitalOptions()),
  });

  return { hospitals: data ?? [], isLoading, isError };
}

/** Countries for the hospital forms. Catalogue-sized and stable. */
export function useCountryOptions() {
  const { data, isLoading, isError } = useQuery<ISelectOption[]>({
    queryKey: ["admin-select-countries"],
    queryFn: async () =>
      ensureOk(await selectValuesService.getCountryOptions()),
  });

  return { countries: data ?? [], isLoading, isError };
}

/**
 * Wards of one hospital group. Idle until a group is chosen, so a ward select
 * can cascade from a hospital select the way Retool's change-event chain did.
 */
export function useWardOptions(hospitalGroupId: number | undefined | null) {
  const { data, isLoading, isError } = useQuery<ISelectOption[]>({
    queryKey: ["admin-select-wards", hospitalGroupId],
    queryFn: async () =>
      ensureOk(await selectValuesService.getWardOptions(hospitalGroupId!)),
    enabled: Boolean(hospitalGroupId),
  });

  return { wards: data ?? [], isLoading, isError };
}

/**
 * Every ward, unfiltered by hospital group — the picker for a page that
 * attaches something to a ward directly (Studies, Ward Measurement Types)
 * rather than cascading from a hospital.
 */
export function useAllWardOptions() {
  const { data, isLoading, isError } = useQuery<ISelectOption[]>({
    queryKey: ["admin-select-wards", "all"],
    queryFn: async () =>
      ensureOk(await selectValuesService.getAllWardOptions()),
  });

  return { wards: data ?? [], isLoading, isError };
}

/**
 * Device types for the add-device form (EN-5166). Guid-keyed, and already
 * filtered to the non-archived ones by the endpoint.
 */
export function usePatientDeviceTypeOptions() {
  const { data, isLoading, isError } = useQuery<IDeviceTypeOption[]>({
    queryKey: ["admin-select-patient-device-types"],
    queryFn: async () =>
      ensureOk(await selectValuesService.getPatientDeviceTypeOptions()),
  });

  return { deviceTypes: data ?? [], isLoading, isError };
}

/** Condition types for the Studies form. Catalogue-sized and stable. */
export function useConditionTypeOptions() {
  const { data, isLoading, isError } = useQuery<ISelectOption[]>({
    queryKey: ["admin-select-condition-types"],
    queryFn: async () =>
      ensureOk(await selectValuesService.getConditionTypeOptions()),
  });

  return { conditionTypes: data ?? [], isLoading, isError };
}

/**
 * Roles a staff member can be given. Comes from the Global role catalogue so
 * a role added by a seed migration shows up without a portal release —
 * Retool hard-coded the list in each form.
 */
export function useRoleOptions() {
  const { data, isLoading, isError } = useQuery<IRoleOption[]>({
    queryKey: ["admin-select-roles"],
    queryFn: async () => ensureOk(await selectValuesService.getRoleOptions()),
  });

  return { roles: data ?? [], isLoading, isError };
}
